import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AlertTriangle, Eye, RefreshCw, X } from 'lucide-react';

interface DriftAlertsProps {
  workspaceId: string;
}

const SEVERITY_STYLES: Record<string, { badge: string; border: string }> = {
  high: {
    badge: 'bg-destructive/10 text-destructive border-destructive/30',
    border: 'border-destructive/30 bg-destructive/5',
  },
  medium: {
    badge: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-300/30',
    border: 'border-amber-500/30 bg-amber-500/5',
  },
  low: {
    badge: 'bg-muted text-muted-foreground border-border',
    border: 'border-border/50 bg-muted/20',
  },
};

const DRIFT_TYPE_LABELS: Record<string, string> = {
  scope: 'Scope-Drift',
  priority: 'Prioritäts-Drift',
  metric: 'Metrik-Drift',
  commitment: 'Commitment-Konflikt',
  review: 'Review-Drift',
};

export const DriftAlerts = ({ workspaceId }: DriftAlertsProps) => {
  const queryClient = useQueryClient();

  const { data: driftEvents = [] } = useQuery({
    queryKey: ['drift-events', workspaceId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('decision_drift_events' as any) as any)
        .select('id, decision_id, drift_type, severity, summary_text, status, recommended_action, created_at')
        .eq('workspace_id', workspaceId)
        .in('status', ['open', 'acknowledged'])
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return (data || []) as Array<{
        id: string;
        decision_id: string;
        drift_type: string;
        severity: string;
        summary_text: string;
        status: string;
        recommended_action: string;
        created_at: string;
      }>;
    },
    staleTime: 60_000,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: async (eventId: string) => {
      await (supabase.from('decision_drift_events' as any) as any)
        .update({ status: 'acknowledged' })
        .eq('id', eventId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['drift-events'] }),
  });

  const resolveMutation = useMutation({
    mutationFn: async (eventId: string) => {
      await (supabase.from('decision_drift_events' as any) as any)
        .update({ status: 'resolved', resolved_at: new Date().toISOString() })
        .eq('id', eventId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['drift-events'] }),
  });

  if (driftEvents.length === 0) return null;

  return (
    <div className="space-y-2">
      {driftEvents.map(event => {
        const styles = SEVERITY_STYLES[event.severity] || SEVERITY_STYLES.low;
        return (
          <div
            key={event.id}
            className={cn('flex items-start gap-3 p-3 rounded-xl border transition-all', styles.border)}
          >
            <AlertTriangle className={cn(
              'h-4 w-4 mt-0.5 flex-shrink-0',
              event.severity === 'high' ? 'text-destructive' : 'text-amber-600 dark:text-amber-400'
            )} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', styles.badge)}>
                  {DRIFT_TYPE_LABELS[event.drift_type] || event.drift_type}
                </Badge>
                <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', styles.badge)}>
                  {event.severity}
                </Badge>
              </div>
              <p className="text-xs text-foreground leading-relaxed">{event.summary_text}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {event.status === 'open' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted"
                  onClick={() => acknowledgeMutation.mutate(event.id)}
                  title="Bestätigen"
                >
                  <Eye className="h-3 w-3" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 hover:bg-muted"
                onClick={() => resolveMutation.mutate(event.id)}
                title="Erledigt"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
